import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

import AuthRoutes from "Routes/Components/Auth";

import { HTTPErrorCallback } from "Helpers/Error";
import {
  currencyMask,
  defaultCurrencyMask,
  formatCurrency,
  parseLocaleNumber,
} from "Helpers/Format";
import moment from "Helpers/MomentCompat";
import { showNotification } from "Helpers/Toast";

import UseAuth from "Hooks/UseAuth";
import UseFile from "Hooks/UseFile";
import { UseGlobalLoading } from "Hooks/UseGlobalLoading";
import UseServices from "Hooks/UseServices";

import Button from "Components/Global/Buttons";
import Input from "Components/Global/Form/Input";
import GetIcon from "Components/Global/Icons";

import * as S from "./styles";

import { useTheme } from "styled-components";

// readOnly: MODO RESTRITO do patrocinador (vínculo ainda não aportado). O
// cabeçalho só exibe o nome e a capa — não permite trocar a imagem nem editar
// valores e, desde 18/08/2026, também NÃO mostra prazo/lei/UF/valores.
//
// ⚠️ Não é só estética: o backend deixou de enviar esses campos no conjunto
// "dados do PPT", então renderizá-los produziria "N/A" e "R$ null". Local e Saldo
// a captar aparecem no corpo (componente Simplified), como no slide.
//
// PATROCINADOR APORTADO (D3/D4 da parte 2, 03/09/2026): o bloco de infos é
// DIFERENTE do do proponente — sai "Prazo de captação", sai "Valor disponível
// para Captação", "Valor captado" vira **"Valor aportado"** com a soma das cotas
// do vínculo (`sponsor_contributed_value`, do backend) e entra o período do
// projeto na última linha. O bloco do PROPONENTE fica intacto de propósito: ele
// usa o lápis para editar `investiment_value`, e o pedido do cliente era só
// sobre o "Dashboard do Patrocinador".
const ProjectHeader = ({ id, projectData, getProject, readOnly = false }) => {
  const theme = useTheme();

  const { authorized, parsedJWT } = UseAuth();
  const { api } = UseServices();
  const { setLoading } = UseGlobalLoading();
  const navigate = useNavigate();
  const { uploadFile } = UseFile();

  const [isEditing, setIsEditing] = useState(false);

  const [investimentValue, setInvestimentValue] = useState(
    projectData?.investiment_value
      ? defaultCurrencyMask(projectData?.investiment_value, true)
      : null,
  );

  const [captureBalance, setCaptureBalance] = useState(
    projectData?.capture_balance
      ? defaultCurrencyMask(projectData?.capture_balance, true)
      : null,
  );

  useEffect(() => {
    if (!authorized) {
      navigate(AuthRoutes.Dashboard.path);
    }
  }, [authorized]);

  useEffect(() => {
    if (!projectData) return;

    setInvestimentValue(
      defaultCurrencyMask(projectData?.investiment_value, true),
    );

    setCaptureBalance(defaultCurrencyMask(projectData?.capture_balance, true));
  }, [projectData]);

  useEffect(() => {
    const new_capture_balance = (
      parseLocaleNumber(projectData?.total_authorized_value?.toString()) -
      parseLocaleNumber(investimentValue?.toString())
    ).toFixed(2);

    if (new_capture_balance < 0) return setCaptureBalance(0);

    setCaptureBalance(defaultCurrencyMask(new_capture_balance, true));
  }, [investimentValue]);

  const updateProjectDetails = async () => {
    setLoading(true);
    try {
      const data = {};
      data.investiment_value = parseLocaleNumber(investimentValue);
      data.capture_balance = parseLocaleNumber(captureBalance);

      await api.post(`/projects/update-project-base-infos`, {
        project_id: id,
        ...data,
      });

      showNotification(
        "success",
        "Sucesso!",
        "Projeto atualizado com sucesso!",
      );

      getProject(id);

      setIsEditing(false);

      setLoading(false);
    } catch (error) {
      setLoading(false);
      HTTPErrorCallback(error);
    }
  };

  const is_sponsor = parsedJWT?.user?.is_sponsor;

  const has_image =
    projectData?.project_image ||
    (projectData?.project_images && projectData?.project_images[0]);

  // Última linha do cabeçalho do patrocinador (D4). Sem um dos extremos o
  // projeto não tem período definido — exibe "—", nunca "Invalid date".
  const getProjectPeriod = () => {
    const from = projectData?.project_start_at;
    const to = projectData?.project_finish_at;

    if (!from || !to) return "—";

    return `${moment(from).format("DD/MM/YYYY")} – ${moment(to).format(
      "DD/MM/YYYY",
    )}`;
  };

  const getRealizationUfs = (realization_address) => {
    if (!realization_address) return "N/A";

    try {
      const parsed =
        typeof realization_address === "string"
          ? JSON.parse(realization_address)
          : realization_address;

      const ufs = Object.keys(parsed);

      return ufs.length ? ufs.join(", ") : "N/A";
    } catch (error) {
      return "N/A";
    }
  };

  const inputRef = useRef(null);

  useEffect(() => {
    const header_image_el = document.getElementById("header-image");

    if (header_image_el) {
      // add hover event listener
      header_image_el.addEventListener("mouseover", () => {
        const changeHeaderImage = document.getElementById("change-image");
        // No modo readOnly o botão de troca não é renderizado.
        if (!has_image || !changeHeaderImage) return null;
        changeHeaderImage.style.opacity = 1;
      });

      header_image_el.addEventListener("mouseleave", () => {
        const changeHeaderImage = document.getElementById("change-image");
        if (!has_image || !changeHeaderImage) return null;
        changeHeaderImage.style.opacity = 0;
      });
    }
  }, [has_image]);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    const reader = new FileReader();

    reader.readAsDataURL(file);

    updateHeaderImageSrc(file);
  };

  const updateHeaderImageSrc = async (img) => {
    setLoading(true);

    try {
      const image_location = await uploadFile(img);

      await api.put("/projects/update-project-image", {
        project_id: id,
        image_src: image_location,
      });

      setLoading(false);

      getProject(id);
    } catch (error) {
      setLoading(false);
      HTTPErrorCallback(error);
    }
  };

  return (
    <S.ProjectHeader>
      <S.ProjectHeaderImage id="header-image">
        {has_image ? (
          <img
            src={projectData?.project_image || projectData?.project_images[0]}
            alt="Imagem do projeto"
          />
        ) : null}

        {/* Trocar a capa é do PROPONENTE. O patrocinador via o overlay (só
            `readOnly` o escondia, e isso cobre apenas o modo restrito), subia o
            arquivo ao S3 e recebia "Projeto não encontrado" — updateProjectImage
            filtra por creator_id. Achado F2 do refinamento. */}
        {!readOnly && !is_sponsor ? (
          <>
            <S.ChangeHeaderImage
              id="change-image"
              hasImage={has_image}
              onClick={() => inputRef?.current?.click()}
            >
              <span>Alterar imagem do projeto</span>
            </S.ChangeHeaderImage>

            <input type="file" onChange={handleFileUpload} ref={inputRef} />
          </>
        ) : null}
      </S.ProjectHeaderImage>

      <S.Infos>
        <h1>{projectData?.project_name}</h1>

        {!readOnly ? (
          <>
            {/* PATROCINADOR (D3/D4): captação não interessa a quem já aportou —
                o que ele quer ver é quanto aportou e quando o projeto acontece. */}
            {is_sponsor ? (
              <>
                <span>
                  Lei de incentivo:{" "}
                  <strong>{projectData?.project_modality}</strong>
                </span>
                <span>
                  UF:{" "}
                  <strong>
                    {getRealizationUfs(projectData?.realization_address)}
                  </strong>
                </span>
                <span>
                  Valor aportado:{" "}
                  <strong>
                    {formatCurrency(projectData?.sponsor_contributed_value)}
                  </strong>
                </span>
                <span>
                  Período do projeto: <strong>{getProjectPeriod()}</strong>
                </span>
              </>
            ) : (
              <>
                <span>
                  Prazo de captação:{" "}
                  <strong>
                    {moment(projectData?.resource_finish_at).format(
                      "DD/MM/YYYY",
                    )}
                  </strong>
                </span>
                <span>
                  Lei de incentivo:{" "}
                  <strong>{projectData?.project_modality}</strong>
                </span>
                <span>
                  UF:{" "}
                  <strong>
                    {getRealizationUfs(projectData?.realization_address)}
                  </strong>
                </span>

                <S.RowText>
                  <p>Valor captado: </p>
                  {!isEditing ? (
                    <strong>
                      {formatCurrency(projectData?.investiment_value)}
                    </strong>
                  ) : (
                    <Input
                      name="investiment_value"
                      type="text"
                      value={investimentValue}
                      onChange={(e) =>
                        setInvestimentValue(currencyMask(e.target.value))
                      }
                      size="xs"
                    />
                  )}
                  <GetIcon
                    name={isEditing ? "X" : "Edit"}
                    size="xs"
                    color={theme.colors.neutral.cinza200}
                    onClick={() => setIsEditing(!isEditing)}
                  />
                </S.RowText>
                <span>
                  Valor disponível para Captação:{" "}
                  <strong>R$ {captureBalance}</strong>
                </span>
              </>
            )}
          </>
        ) : null}

        {isEditing && (
          <Button
            type="button"
            text="Atualizar"
            onClick={() => {
              updateProjectDetails();
            }}
          />
        )}
      </S.Infos>
    </S.ProjectHeader>
  );
};

export default ProjectHeader;
